import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserShield,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaSignInAlt,
  FaKey,
  FaEnvelope,
} from "react-icons/fa";
import "./AdminLogin.css";
import { BASE_URL } from "../../Config/config";

export default function AdminLogin() {
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [forgotData, setForgotData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const handleForgotChange = (e) => {
    setForgotData({
      ...forgotData,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // LOGIN
  // =========================

  const handleLogin = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!loginData.email.trim()) {
      setError("Please enter email address.");
      return;
    }

    if (!loginData.password) {
      setError("Please enter password.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/admin_auth.php?action=login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: loginData.email.trim(),
          password: loginData.password,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message || "Invalid email or password.");
        return;
      }

      sessionStorage.setItem("admin_logged_in", "true");
      sessionStorage.setItem("admin_id", data.admin.id);
      sessionStorage.setItem("admin_username", data.admin.username || "");
      sessionStorage.setItem("admin_name", data.admin.name || "");
      sessionStorage.setItem("admin_email", data.admin.email || "");

      navigate("/MyAdminStar/dashboard");
    } catch (err) {
      console.error(err);
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };
  // =========================
  // FORGOT PASSWORD
  // =========================

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!forgotData.email.trim()) {
      setError("Please enter email address.");
      return;
    }

    if (!forgotData.newPassword) {
      setError("Please enter new password.");
      return;
    }

    if (forgotData.newPassword.length < 6) {
      setError("Password must contain at least 6 characters.");
      return;
    }

    if (!forgotData.confirmPassword) {
      setError("Please confirm your password.");
      return;
    }

    if (forgotData.newPassword !== forgotData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${BASE_URL}/admin_auth.php?action=forgot_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: forgotData.email.trim(),
            newPassword: forgotData.newPassword,
          }),
        },
      );

      const data = await response.json();

      if (!data.success) {
        setError(data.message || "Unable to reset password.");
        return;
      }

      setMessage("Password changed successfully. You can now login.");

      setForgotData({
        email: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setMode("login");
        setMessage("");
      }, 1500);
    } catch (err) {
      console.error(err);
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        {/* LEFT SIDE */}
        <div className="admin-login-left">
          <div className="admin-brand">
            <div className="brand-icon">
              <FaUserShield />
            </div>

            <h1>Admin Panel</h1>

            <p>Secure administration portal</p>
          </div>

          <div className="admin-login-info">
            <div className="info-icon">
              <FaKey />
            </div>

            <h2>Welcome Back</h2>

            <p>
              Login to manage your website, users, products and other
              administrative features.
            </p>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="admin-login-right">
          {/* =========================
              LOGIN
          ========================= */}

          {mode === "login" ? (
            <>
              <div className="login-heading">
                <h2>Admin Login</h2>

                <p>Enter your credentials to continue</p>
              </div>

              {error && <div className="auth-message error">{error}</div>}

              {message && <div className="auth-message success">{message}</div>}

              <form onSubmit={handleLogin}>
                {/* USERNAME */}

                <div className="input-group">
                  <label>Email Address</label>

                  <div className="input-wrapper">
                    <FaEnvelope />

                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={loginData.email}
                      onChange={handleLoginChange}
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* PASSWORD */}

                <div className="input-group">
                  <label>Password</label>

                  <div className="input-wrapper">
                    <FaLock />

                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* FORGOT */}

                <div className="login-options">
                  <button
                    type="button"
                    className="forgot-button"
                    onClick={() => {
                      setMode("forgot");
                      setError("");
                      setMessage("");
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* LOGIN BUTTON */}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    "Logging in..."
                  ) : (
                    <>
                      <FaSignInAlt />
                      Login
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* =========================
               FORGOT PASSWORD
            ========================= */

            <>
              <div className="login-heading">
                <div className="forgot-icon">
                  <FaKey />
                </div>

                <h2>Forgot Password</h2>

                <p>Enter your email address and create a new password.</p>
              </div>

              {error && <div className="auth-message error">{error}</div>}

              {message && <div className="auth-message success">{message}</div>}

              <form onSubmit={handleForgotPassword}>
                {/* USERNAME */}

                <div className="input-group">
                  <label>Email Address</label>

                  <div className="input-wrapper">
                    <FaEnvelope />

                    <input
                      type="email"
                      name="email"
                      placeholder="Enter admin email address"
                      value={forgotData.email}
                      onChange={handleForgotChange}
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* NEW PASSWORD */}

                <div className="input-group">
                  <label>New Password</label>

                  <div className="input-wrapper">
                    <FaLock />

                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      placeholder="Enter new password"
                      value={forgotData.newPassword}
                      onChange={handleForgotChange}
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}

                <div className="input-group">
                  <label>Confirm Password</label>

                  <div className="input-wrapper">
                    <FaLock />

                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      value={forgotData.confirmPassword}
                      onChange={handleForgotChange}
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* CHANGE PASSWORD */}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? "Changing Password..." : "Change Password"}
                </button>

                {/* BACK */}

                <button
                  type="button"
                  className="back-login-button"
                  onClick={() => {
                    setMode("login");
                    setError("");
                    setMessage("");
                  }}
                >
                  <FaArrowLeft />
                  Back to Login
                </button>
              </form>
            </>
          )}

          <div className="login-footer">
            © {new Date().getFullYear()} Admin Panel. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}
